/* Decision application — the ONE implementation of "replay a validated per-round
 * decision bundle onto a live game object".
 *
 * Both the server (apps/api, at POST /games/:id/advance) and the client preview
 * (apps/web buildWorkingGame, on every keystroke) call applyDecisions() from
 * here, so what a student previews is — by construction, not by discipline —
 * exactly what the server resolves. Before this module the two sides carried
 * hand-synchronised copies that had already drifted (financing order, suppliers,
 * R&D, product numbering).
 *
 * Every step drives the engine's OWN mutators (newProduct, enterMethod,
 * buildStores, setFlagship, startDraft/commitDraft, writeOff, …) — the exact paths
 * the prototype UI used — so the server reaches feature parity without
 * re-implementing game logic. Individual lever failures (not enough cash, a
 * locked component, an unknown id) are swallowed the same way the prototype UI
 * would simply refuse the click; Zod upstream has already guaranteed the shape
 * of every value, and the sanitisers here guarantee its *meaning* (ids that
 * exist, components that are unlocked, R&D prerequisites that are met).
 *
 * This file is NOT covered by the golden-master fixture (that pins
 * engine.core.js); packages/engine/test/apply.test.js pins it instead.
 */
import {
  Engine, newProduct, applyGameCfg, instrEnabled, clamp,
  CITIES, SEGMENTS, BRAND_STRATS, QUARTER_NAMES,
  COMPONENTS, SUPPLIERS, AD_CHANNELS, CREATIVES, COUNTRIES, TECH_BY_ID, ACTIVE,
} from './engine.core.js';

/** Working-game id of a product staged in this round's `newProducts` bundle:
 *  `'new:<index>'`. The client preview assigns these ids (opts.preview) and the
 *  server resolves them onto the products it creates from the same bundle, so
 *  same-quarter Pricing/Production/Marketing edits on a brand-new product resolve
 *  exactly as previewed. (@venturewrist/shared re-declares the same literal.) */
export const NEW_PRODUCT_ID_PREFIX = 'new:';
export function newProductIndex(id) {
  if (typeof id !== 'string' || !id.startsWith(NEW_PRODUCT_ID_PREFIX)) return null;
  const n = Number(id.slice(NEW_PRODUCT_ID_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

const isStr = (v) => typeof v === 'string' && v.length > 0;
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const clampNum = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const cityById = (id) => CITIES.find((c) => c.id === id);
/** Drop a retired SKU's units from every warehouse — the ledger only walks live
 *  SKUs, so stock left behind by a write-off or retire would hold capacity and
 *  count as "positioned" forever. */
function purgeWarehouseStock(g, key) {
  for (const w of g.warehouses || []) if (w.stock && key in w.stock) delete w.stock[key];
}
/** A country that is in play for this game (applyGameCfg narrows the set). */
const inPlay = (cc) => isStr(cc) && ACTIVE().some((c) => c.id === cc);

/* The prototype only ever fed the engine through its sliders, so these are its
 * slider ranges; the API accepts a wider schema and clamps here. Outside them
 * the formulas still run but stop meaning anything ($0 watches, a $100M R&D
 * line on a $20M company). */
export const PRICE_RANGE = [49, 899];
export const MKT_BUDGET_MAX_M = 6;      // $M per model per quarter
export const RND_BUDGET_MAX = 4e6;      // $ per quarter
/** Equity: one raise per quarter, at most this share of market cap (or book
 *  equity if larger) — an uncapped raise at 0.94× followed by a buyback at
 *  1.04× of the diluted price was a money pump. Buybacks: at most this share of
 *  shares outstanding per quarter. */
export const EQUITY_RAISE_MAX_FRAC = 0.5;
export const BUYBACK_MAX_FRAC = 0.10;

/* ── Sanitisers (pure) ──────────────────────────────────────────────────── */

/** True if `id` is a supplier of category `cat` (engine SUPPLIERS). */
export function validSupplier(cat, id) {
  return isStr(id) && (SUPPLIERS[cat] || []).some((s) => s.id === id);
}

/** Keep only the spec fields that name a real component the company has
 *  unlocked (a `lock` component needs its tech in rnd.done — the same rule the
 *  Design Studio greys pills out with). Unknown/locked fields are dropped, never
 *  substituted. */
export function sanitizeSpec(g, spec) {
  const out = {};
  if (!spec || typeof spec !== 'object') return out;
  const done = (g && g.rnd && g.rnd.done) || [];
  const unlocked = (cat, id) => {
    const c = (COMPONENTS[cat] || []).find((x) => x.id === id);
    return !!c && (!c.lock || done.includes(c.lock));
  };
  for (const cat of ['cpu', 'display', 'battery', 'casing', 'strap']) {
    if (isStr(spec[cat]) && unlocked(cat, spec[cat])) out[cat] = spec[cat];
  }
  if (isStr(spec.size) && COMPONENTS.sizes.some((x) => x.id === spec.size)) out.size = spec.size;
  if (isStr(spec.style) && COMPONENTS.style.some((x) => x.id === spec.style)) out.style = spec.style;
  if (isStr(spec.color) && COMPONENTS.colors.some((x) => x.id === spec.color)) out.color = spec.color;
  if (Array.isArray(spec.sensors)) {
    const seen = new Set(), arr = [];
    for (const id of spec.sensors) {
      if (isStr(id) && !seen.has(id) && unlocked('sensors', id)) { seen.add(id); arr.push(id); }
    }
    out.sensors = arr;
  }
  return out;
}

/** Keep only marketing fields naming a real channel / creative / country. */
export function sanitizeMarketing(mkt) {
  if (!mkt || typeof mkt !== 'object') return null;
  const out = {};
  const b = num(mkt.budget);
  if (b != null) out.budget = clampNum(b, 0, MKT_BUDGET_MAX_M);
  if (isStr(mkt.channel) && AD_CHANNELS.some((c) => c.id === mkt.channel)) out.channel = mkt.channel;
  if (isStr(mkt.creative) && CREATIVES.some((c) => c.id === mkt.creative)) out.creative = mkt.creative;
  if (Array.isArray(mkt.countries)) {
    const seen = new Set(), arr = [];
    for (const cc of mkt.countries) {
      if (isStr(cc) && !seen.has(cc) && COUNTRIES.some((c) => c.id === cc)) { seen.add(cc); arr.push(cc); }
    }
    out.countries = arr;
  }
  return out;
}

/* ── Brand strategy (Cap2 ch.7) ─────────────────────────────────────────── */

/* The prototype's UI.setBrandStrat minus its confirm()/toast/render. Mid-game:
 * reset every brand rating to zero (company, per-segment awareness, per-product
 * loyalty/awareness, per-country stores). Freshly founded (no resolved history):
 * migrate the starting brand onto the per-product model without penalty. */
export function applyBrandStrat(g, strat) {
  if (!BRAND_STRATS.some((b) => b.id === strat)) return;
  if ((g.brandStrat || 'range') === strat) return;
  if (g.history && g.history.length) {
    g.brand = 0;
    for (const sid in g.awareness) g.awareness[sid] = 0;
    g.brandCo = null;
    for (const m of g.products) {
      m.bLoyal = 0;
      m.bAw = Object.fromEntries(SEGMENTS.map((x) => [x.id, 0]));
      m.brandCo = null;
    }
    const name = (BRAND_STRATS.find((b) => b.id === strat) || {}).name || strat;
    g.news = g.news || [];
    g.news.unshift({
      icon: '🏷️', when: QUARTER_NAMES(g.round),
      text: `Brand strategy switched to ${name} — all brand ratings reset to zero. Loyalty and awareness must be rebuilt from scratch.`,
    });
  } else {
    g.brandCo = null;
    for (const m of g.products) { m.bLoyal = g.brand; m.bAw = { ...g.awareness }; m.brandCo = null; }
  }
  g.brandStrat = strat;
}

/* ── Financing (DESIGN-corporate-finance) ───────────────────────────────── */

const LOAN_TENORS = [4, 8, 12];
const BOND_TENORS = [12, 16, 20];
const BUYBACK_CASH_BUFFER = 2e6; // prototype: "Keep a 2M cash buffer"

/* Replays the prototype's takeLoan / issueBond / raiseEquity / buyback /
 * setDivRatio onto the live game, including every guard those handlers applied
 * before mutating: the D/E borrowing covenant, bond eligibility (book equity
 * above the threshold and an investment-grade rating), the buyback cash buffer,
 * and the instructor's instrument toggles. */
export function applyFinancing(g, f) {
  if (!f || typeof f !== 'object') return;
  Engine.ensureStock(g); Engine.ensureFinance(g);
  const H = g.history;
  // Insolvency watch (DESIGN-insolvency-and-direct-fulfilment §4.3): debt markets, buybacks
  // and dividends are closed until book equity is positive again. An equity raise stays open.
  const insolvent = !!g.insolvent;
  if (instrEnabled(g, 'loan') && !insolvent) {
    (f.loans || []).forEach((ln, i) => {
      const amount = num(ln && ln.amount), q = ln && ln.tenor;
      if (amount == null || amount <= 0 || !LOAN_TENORS.includes(q)) return;
      if (amount > Engine.borrowCapacity(g, H)) return;
      const rate = Engine.loanRate(g, H, q), amort = q === 12 ? 'bullet' : 'equal';
      const label = q === 4 ? 'Short-term note' : q === 8 ? 'Term loan' : 'Long-term loan';
      g.debts.push({ id: 'loan-' + g.round + '-' + i, kind: 'loan', label, face: amount, rateQ: rate, qLeft: q, qTotal: q, amort, issuedRound: g.round });
      g.cash += amount;
    });
  }
  if (instrEnabled(g, 'bond')) {
    (f.bonds || []).forEach((bd, i) => {
      const face = num(bd && bd.face), q = bd && bd.tenor;
      if (face == null || face <= 0 || !BOND_TENORS.includes(q)) return;
      const eq = Engine.bookEquity(g), rating = Engine.creditRating(g, H);
      if (eq < (g.bondThreshold || 40e6) || rating === 'B' || rating === 'CCC') return;
      const capB = Math.max(0, 3 * eq - Engine.totalDebt(g));
      if (face > capB) return;
      const rate = Engine.loanRate(g, H, q, true);
      g.debts.push({ id: 'bond-' + g.round + '-' + i, kind: 'bond', label: 'Bond', face, rateQ: rate, qLeft: q, qTotal: q, amort: 'bullet', issuedRound: g.round });
      g.cash += face * 0.98;
    });
  }
  let raise = num(f.equityRaise);
  if (instrEnabled(g, 'equity') && raise != null && raise > 0) {
    const price = g.stockPrice || 10, issueP = price * 0.94;
    const maxRaise = EQUITY_RAISE_MAX_FRAC * Math.max(g.shares * price, Engine.bookEquity(g), 0);
    raise = Math.min(raise, maxRaise);
    const ns = Math.round(raise / issueP);
    if (ns > 0) { g.shares += ns; g.paidIn += raise; g.cash += raise; }
  }
  const spend = num(f.buyback);
  if (instrEnabled(g, 'buyback') && !insolvent && spend != null && spend > 0 && spend <= g.cash - BUYBACK_CASH_BUFFER) {
    const price = g.stockPrice || 10, buyP = price * 1.04;
    let bs = Math.min(Math.round(spend / buyP), Math.floor(g.shares * BUYBACK_MAX_FRAC));
    const newTot = Math.max(g.ownerShares, g.shares - bs); bs = g.shares - newTot;
    if (bs > 0) {
      const realSpend = bs * buyP;
      g.shares = newTot; g.cash -= realSpend;
      // Retiring shares above paid-in capital comes out of retained earnings,
      // so book equity falls by the full spend (it drives leverage, rating and
      // borrowing capacity).
      const excess = Math.max(0, realSpend - g.paidIn);
      g.paidIn = Math.max(0, g.paidIn - realSpend);
      if (excess > 0) g.cumProfit = (g.cumProfit || 0) - excess;
    }
  }
  const ratio = num(f.divRatio);
  if (insolvent) g.divRatio = 0;
  else if (instrEnabled(g, 'dividend') && ratio != null) g.divRatio = clamp(ratio, 0, 0.8);
  Engine.ensureStock(g);
}

/* ── R&D ─────────────────────────────────────────────────────────────────── */

/* Budget + active node + queue. The engine's advance() starts the next queued
 * node without re-checking prerequisites, so prerequisites are enforced HERE:
 * the active node needs every prerequisite already done, and each queued node
 * needs its prerequisites in done ∪ active ∪ the queue ahead of it (the same
 * rule the R&D screen's canQueue applies). The stored queue is re-validated even
 * when the bundle carries no R&D edits, so stopping the active node can never
 * let a dependent queued node start without it. */
export function applyRnd(g, r) {
  Engine.normalizeRnd(g);
  const rnd = g.rnd, done = rnd.done || [];
  const known = (id) => isStr(id) && !!TECH_BY_ID[id];
  r = r && typeof r === 'object' ? r : {};
  const b = num(r.budget);
  if (b != null) rnd.budget = clampNum(b, 0, RND_BUDGET_MAX);
  if (r.active === null) rnd.active = null;
  else if (r.active !== undefined && known(r.active) && !done.includes(r.active)
    && TECH_BY_ID[r.active].req.every((q) => done.includes(q))) rnd.active = r.active;
  const src = Array.isArray(r.queue) ? r.queue : (rnd.queue || []);
  const have = new Set(done);
  if (rnd.active) have.add(rnd.active);
  const queue = [];
  for (const id of src) {
    if (!known(id) || have.has(id)) continue;
    if (!TECH_BY_ID[id].req.every((q) => have.has(q))) continue;
    have.add(id); queue.push(id);
  }
  rnd.queue = queue;
}

/* ── Production + logistics network ─────────────────────────────────────── */

/* Replay the Production + Logistics screens' network intents through the
 * engine's own helpers. Factory and warehouse ids are deterministic (no
 * Math.random — the server must resolve identically to the preview) and unique
 * across rounds. State transitions follow the prototype's UI: a factory still
 * under construction offers no actions, an active one can expand or mothball,
 * a mothballed one can only reactivate. */
export function applyProductionNetwork(g, d) {
  Engine.ensureMfgNet(g);
  Engine.ensureLogistics(g);

  // Logistics network — applied before whAlloc so a freshly built/expanded
  // warehouse can receive shipment this same quarter.
  if (d.freightMode === 'absorb' || d.freightMode === 'passthrough') g.freightMode = d.freightMode;
  (d.whBuilds || []).forEach((b, i) => {
    const c = b && cityById(b.city), cap = num(b && b.cap);
    if (!c || !(c.whFit > 0) || cap == null || cap <= 0) return;
    const capex = Engine.whCapex(b.city, cap);
    if (g.cash < capex) return;
    g.cash -= capex;
    const ready = g.round + Engine.whBuildQuarters(cap);
    g.warehouses = g.warehouses || [];
    g.warehouses.push({ id: 'wh_' + b.city + '_' + g.round + '_' + i, city: b.city, capacity: cap, built: g.round, readyRound: ready, status: 'building' });
  });
  for (const [whId, delta] of Object.entries(d.whExpand || {})) {
    const w = (g.warehouses || []).find((x) => x.id === whId);
    const add = num(delta);
    if (!w || add == null || add <= 0) continue;
    const cost = 0.55 * add * ((cityById(w.city) || { buildIdx: 1 }).buildIdx);
    if (g.cash < cost) continue;
    g.cash -= cost; w.capacity += add;
  }
  for (const id of d.whClose || []) g.warehouses = (g.warehouses || []).filter((w) => w.id !== id);

  // Generation lifecycle: scrap inventory / resume a paused generation. The
  // lifecycle is one-directional (a wound-down gen never restarts) and a gen
  // still in engineering has nothing to scrap or resume.
  for (const op of d.genOps || []) {
    const m = g.products.find((x) => x.id === (op && op.productId));
    const gv = m && m.gens.find((x) => x.gen === op.gen);
    if (!gv || gv.dev) continue;
    if (op.op === 'writeoff') { Engine.writeOff(g, m, gv); purgeWarehouseStock(g, m.id + '-' + gv.gen); }
    else if (op.op === 'resume' && !gv.wound) gv.production = 20;
  }

  // Build new owned factories (charged now; come online after a build lead time).
  (d.factoryBuilds || []).forEach((b, i) => {
    const c = b && cityById(b.city), cap = num(b && b.cap);
    if (!c || !(c.factoryFit > 0) || cap == null || cap <= 0) return;
    const setup = Engine.setupCost(b.city, cap);
    if (g.cash < setup) return;
    g.cash -= setup;
    const ready = g.round + Engine.factoryBuildQuarters(cap);
    g.factories = g.factories || [];
    g.factories.push({
      id: 'factory' + (g.factories.length + 1) + '-' + g.round + '-' + i, city: b.city, capacity: cap,
      builtRound: ready, readyRound: ready, orderedRound: g.round, status: 'building', ramp: 0,
    });
  });

  // Expand / mothball / reactivate existing factories — one op per factory per
  // quarter (the UI stages one; forty instant +50k expansions would dodge the
  // build lead time a big factory pays).
  const seenFactory = new Set();
  for (const op of d.factoryOps || []) {
    const p = (g.factories || []).find((x) => x.id === (op && op.id));
    if (!p || seenFactory.has(p.id)) continue;
    seenFactory.add(p.id);
    const building = p.status === 'building', moth = p.status === 'mothballed';
    if (op.op === 'expand' && !building && !moth) {
      const inc = 16e3 * 50 * ((cityById(p.city) || { buildIdx: 1 }).buildIdx);
      if (g.cash < inc) continue;
      g.cash -= inc; p.capacity += 50;
    } else if (op.op === 'mothball' && !building && !moth) {
      p.status = 'mothballed';
      for (const m of Engine.activeModels(g)) for (const gv of Engine.prodGens(m)) gv.alloc = null;
    } else if (op.op === 'reactivate' && moth) {
      p.status = 'active';
    }
  }

  // Negotiated partner volume commitments.
  for (const c of d.contracts || []) {
    const o = c && Engine.outsourcerById(g, c.partnerId);
    const qty = num(c && c.qty), dur = num(c && c.dur);
    if (!o || qty == null || dur == null || qty <= 0 || dur <= 0) continue;
    if (o.relationship < 45 && dur < 6) continue; // partner won't sign a short cold-relationship deal
    const discount = clamp((0.02 + dur * 0.005 + qty / 120 * 0.01) * (0.6 + o.relationship / 100), 0.02, 0.08);
    const capAdd = Math.round(qty * 1.4);
    o.capacity = Math.max(o.capacity, capAdd);
    o.lastExpansion = { round: g.round, amount: capAdd };
    g.mfgNet.contracts.push({ partnerId: c.partnerId, qtyCommit: qty, quartersLeft: dur, discount, penaltyRate: 0.40, startRound: g.round });
  }

  // Free capacity expansions from trusted partners (relationship > 70) — at most
  // one per partner per quarter, however many times the id is listed.
  for (const id of new Set(d.expansions || [])) {
    const o = Engine.outsourcerById(g, id);
    if (!o || o.relationship <= 70) continue;
    const add = Math.round(o.capacity * 0.15);
    o.capacity += add;
    o.lastExpansion = { round: g.round, amount: add };
  }

  // Warehouse online-fulfilment shipment allocation.
  if (d.whAuto) {
    g.whAlloc = null;
  } else if (d.whAlloc && Object.keys(d.whAlloc).length) {
    if (g.whAlloc == null) g.whAlloc = { ...Engine.whSupply(g).byWh }; // freeze current auto values
    for (const [whId, k] of Object.entries(d.whAlloc)) g.whAlloc[whId] = Math.max(0, num(k) || 0) * 1000;
  }
}

/* ── The bundle ─────────────────────────────────────────────────────────── */

/**
 * Apply a validated per-round decision bundle onto the live game object, in
 * place. `opts.preview` marks the client's working-game build: products staged
 * this round get the `'new:<index>'` ids other screens address them by.
 * Everything else is identical on both sides.
 */
export function applyDecisions(g, d, opts) {
  d = d && typeof d === 'object' ? d : {};
  const preview = !!(opts && opts.preview);
  // Make the engine's ambient config (active countries, rival counts) reflect
  // this game before we drive its country-aware mutators.
  applyGameCfg(g);

  // 0) Brand strategy switch (Marketing screen) — applied first so the migration
  //    touches existing products before any new products are seeded this round.
  //    Financing next, so same-quarter loans fund same-quarter builds.
  if (d.brandStrat) applyBrandStrat(g, d.brandStrat);
  if (d.financing) applyFinancing(g, d.financing);

  // 1) Suppliers — straight assignment onto the supplier map (known ids only).
  if (d.suppliers && typeof d.suppliers === 'object') {
    g.suppliers = g.suppliers || {};
    for (const [k, v] of Object.entries(d.suppliers)) {
      if (validSupplier(k, v)) g.suppliers[k] = v;
    }
  }

  // 2) R&D — budget + active + queue; the stored queue is always re-validated.
  applyRnd(g, d.rnd);

  // 3) New products — seed from a segment template, then apply launch settings.
  //    Kept in bundle order: other screens address a product staged this same
  //    round as 'new:<index into d.newProducts>' and step 4 resolves those onto
  //    the products created here.
  const created = [];
  (d.newProducts || []).forEach((np, idx) => {
    if (!np || !SEGMENTS.some((s) => s.id === np.seg)) return;
    const p = newProduct(g, np.seg);
    // newProduct() draws its id from Math.random outside the seeded window, so
    // stamp a deterministic one: the same bundle must yield the same state.
    p.id = preview ? NEW_PRODUCT_ID_PREFIX + idx : 'p' + (g.nextProdNum || 1) + '-r' + g.round + '-' + idx;
    created[idx] = p;
    if (isStr(np.name)) p.name = np.name;
    const gen0 = p.gens[0];
    if (gen0) {
      const price = num(np.price), production = num(np.production);
      if (price != null) gen0.price = clampNum(price, PRICE_RANGE[0], PRICE_RANGE[1]);
      if (production != null) gen0.production = Math.max(0, production);
      // Hardware design choices onto the gen-1 spec (the engine reads gv.spec
      // for cost/quality/attributes).
      if (np.spec) Object.assign(gen0.spec, sanitizeSpec(g, np.spec));
    }
    const mkt = sanitizeMarketing(np.marketing);
    if (mkt) p.mkt = { ...p.mkt, ...mkt };
    if (isStr(np.targetSeg) && SEGMENTS.some((s) => s.id === np.targetSeg)) p.targetSeg = np.targetSeg;
    g.products.push(p);
    g.nextProdNum = (g.nextProdNum || 1) + 1;
  });

  // 4) Allocation policy — like the prototype's UI.setAllocMode, choosing a
  //    policy also resets every manual per-source split so the solver takes
  //    over; runs before the per-generation edits below so a split staged in
  //    the same bundle still wins.
  if (d.allocMode === 'cost' || d.allocMode === 'rel') {
    g.allocMode = d.allocMode;
    for (const m of Engine.activeModels(g)) for (const gv of Engine.prodGens(m)) gv.alloc = null;
  }

  // 5) Existing products — rename, retarget, marketing, per-generation
  //    price/production, proto-gen spec edits, committed new generations, retire.
  for (const pd of d.products || []) {
    if (!pd) continue;
    const idx = newProductIndex(pd.id);
    const m = idx != null ? created[idx] : g.products.find((x) => x.id === pd.id);
    if (!m) continue;
    if (isStr(pd.name)) m.name = pd.name; // renaming the line renames every generation (§5.3)
    if (isStr(pd.targetSeg) && SEGMENTS.some((s) => s.id === pd.targetSeg)) m.targetSeg = pd.targetSeg;
    const mkt = sanitizeMarketing(pd.mkt);
    if (mkt) m.mkt = { ...m.mkt, ...mkt };
    for (const gd of pd.gens || []) {
      const gv = gd && m.gens.find((x) => x.gen === gd.gen);
      if (!gv) continue;
      const price = num(gd.price);
      if (price != null) gv.price = clampNum(price, PRICE_RANGE[0], PRICE_RANGE[1]);
      // Production allocation: a manual per-source split (sum = volume), else a
      // plain volume change re-opens auto-allocation (prototype: gv.alloc=null).
      if (gd.alloc && typeof gd.alloc === 'object') {
        const known = new Set(Engine.prodSources(g).map((s) => s.id));
        const alloc = {};
        for (const [sid, k] of Object.entries(gd.alloc)) {
          const v = num(k);
          if (known.has(sid) && v != null && v >= 0) alloc[sid] = v;
        }
        gv.alloc = alloc;
        gv.production = Math.round(Object.values(alloc).reduce((a, b) => a + b, 0));
      } else {
        const production = num(gd.production);
        if (production != null) { gv.production = Math.max(0, production); gv.alloc = null; }
        else if (gd.alloc === null) gv.alloc = null; // explicit "back to auto" for a persisted split
      }
      // A brand-new model's Gen 1 hardware stays editable until its quarter ends.
      if (gd.spec && gv.proto === g.round) Object.assign(gv.spec, sanitizeSpec(g, gd.spec));
    }
    if (pd.newGen && pd.newGen.spec) {
      // A draft is transient UI state, never persisted on purpose: a stale one
      // (from an earlier commit that failed for cash) would block startDraft
      // forever, so it is cleared before trying again.
      if (m.draft && !Engine.devGen(m)) m.draft = null;
      if (Engine.startDraft(g, m) && m.draft) {
        Object.assign(m.draft.spec, sanitizeSpec(g, pd.newGen.spec));
        if (!Engine.commitDraft(g, m)) m.draft = null;
      }
    }
    if (pd.retire && g.products.filter((x) => x.active).length > 1) {
      m.active = false;
      m.draft = null;
      for (const gv of m.gens || []) purgeWarehouseStock(g, m.id + '-' + gv.gen);
    }
  }

  // 6) Distribution — open a go-to-market method per country (enterMethod
  //    validates the country, the method and the cash).
  for (const e of d.distribution || []) {
    // Check the country here: enterMethod touches Engine.mkt() before its own
    // guard, which would create a market row for an unknown code.
    if (e && inPlay(e.country)) Engine.enterMethod(g, e.country, e.method);
  }

  // 7) Store builds — ensure a subsidiary exists, then add stores (8/qtr cap
  //    and cash check inside buildStores). Closures pay severance per store
  //    (closeStores checks the subsidiary and the count).
  for (const sb of d.storeBuilds || []) {
    if (!sb || !inPlay(sb.country)) continue;
    const count = num(sb.count);
    if (count == null || count <= 0) continue;
    if (!Engine.mkt(g, sb.country).sub) Engine.enterMethod(g, sb.country, 'stores');
    Engine.buildStores(g, sb.country, count);
  }
  for (const sc of d.storeCloses || []) {
    if (!sc || !inPlay(sc.country)) continue;
    const count = num(sc.count);
    if (count == null || count <= 0) continue;
    Engine.closeStores(g, sc.country, Math.floor(count));
  }

  // 8) Flagship stores.
  // A flagship is a store: the engine only checks for a subsidiary, which
  // would grant the coverage and loyalty bonus with zero stores.
  for (const cc of d.flagship || []) {
    if (inPlay(cc) && Engine.mkt(g, cc).n > 0) Engine.setFlagship(g, cc);
  }

  // 9) Production + logistics network.
  applyProductionNetwork(g, d);
}
